// ./src/components/features/EventVendors/VendorRequirementDrawer/VendorRequirementDrawer.tsx
//
// ROADMAP #170 — a document this event asks of a vendor: its type (insurance, a certificate, a permit, a licence…), what
// kind ("General liability" — common kinds one tap away, any words allowed), the planner's instructions to the vendor
// ("Name the City of X as additional insured"), and by when. A URL stage on the vendor's page (`…/documents/add`,
// `…/documents/:requirementId/edit`); the page has the vendor, its requests included, read by id.
// ⚠ Asking for another TYPE takes the document on it off (it no longer answers) — said under the type before saving.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Stack, TextInput, Textarea, Group, Chip, Text } from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useTranslation } from "react-i18next";
import { OpResult, OP_RESULT_CODES } from "@sdflc/api-helpers";

import {
  EventVendorRequirement,
  EventVendorRequirementInput,
  VENDOR_DOCUMENT_KIND_SUGGESTIONS,
  VENDOR_DOCUMENT_TYPE,
  VENDOR_DOCUMENT_TYPE_LABEL,
  VENDOR_LIMITS,
  VendorDocumentType,
} from "@/utils/api";
import { getFormErrors, formatErrors } from "@/utils";
import { useModalShortcuts, useFormSnapshot, SHORTCUT_HINTS } from "@/hooks";
import { AppDrawer, SelectField } from "@/components/generic";
import { useResponsiveDatePickerProps } from "@/components/generic/form";

import { pickerDay } from "../vendorUi";

// Every name here is rendered below (▶ npm run form-audit).
const KNOWN_FORM_FIELDS = ["documentType", "documentKind", "instructions", "dueDate"];

export const VENDOR_DOCUMENT_TYPE_OPTIONS = Object.values(VENDOR_DOCUMENT_TYPE).map((value) => ({ value, label: VENDOR_DOCUMENT_TYPE_LABEL[value] }));

export interface VendorRequirementDrawerProps {
  opened: boolean;
  onClose: () => void;
  /** Null = ask for a new one. */
  requirement: EventVendorRequirement | null;
  vendorName: string;
  onSave: (params: EventVendorRequirementInput) => Promise<OpResult>;
  saving: boolean;
}

interface RequirementForm {
  documentType: string;
  documentKind: string;
  instructions: string;
  dueDate: string | null;
}

const EMPTY_FORM: RequirementForm = { documentType: VENDOR_DOCUMENT_TYPE.INSURANCE, documentKind: "", instructions: "", dueDate: null };

const formOf = (r: EventVendorRequirement): RequirementForm => ({
  documentType: r.documentType ?? VENDOR_DOCUMENT_TYPE.INSURANCE,
  documentKind: r.documentKind ?? "",
  instructions: r.instructions ?? "",
  dueDate: r.dueDate ?? null,
});

export const VendorRequirementDrawer: React.FC<VendorRequirementDrawerProps> = ({ opened, onClose, requirement, vendorName, onSave, saving }) => {
  const { t } = useTranslation(["vendors", "common"]);
  const responsiveDatePicker = useResponsiveDatePickerProps();
  const editingId = requirement?.id ?? null;
  const isEdit = !!editingId;
  const [form, setForm] = useState<RequirementForm>(EMPTY_FORM);
  const [error, setError] = useState<OpResult | null>(null);
  const { isDirty, seedFrom, reseed, clear } = useFormSnapshot(form);

  useEffect(() => {
    if (!opened) {
      clear();
      setError(null);

      return;
    }

    if (!editingId) {
      setForm(EMPTY_FORM);
      seedFrom(EMPTY_FORM);
    }
  }, [opened, editingId, seedFrom, clear]);

  useEffect(() => {
    if (opened && requirement?.id) {
      setForm(reseed(formOf(requirement), requirement.id));
    }
  }, [opened, requirement, reseed]);

  const { fields: fieldErrors, genericErrors } = useMemo(() => getFormErrors(error, KNOWN_FORM_FIELDS), [error]);
  const suggestions = VENDOR_DOCUMENT_KIND_SUGGESTIONS[form.documentType as VendorDocumentType] ?? [];
  const typeChangeDropsDocument = isEdit && !!requirement?.document?.id && form.documentType !== requirement?.documentType;

  const handleSubmit = useCallback(async () => {
    if (saving) {
      return;
    }

    setError(null);
    const result = await onSave({
      documentType: form.documentType,
      documentKind: form.documentKind.trim() || null,
      instructions: form.instructions.trim() || null,
      dueDate: form.dueDate,
    });

    if (result.code !== OP_RESULT_CODES.OK) {
      setError(result);

      return;
    }

    onClose();
  }, [saving, onSave, form, onClose]);

  useModalShortcuts({ onConfirm: handleSubmit, canConfirm: !saving, enabled: opened });

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={
        isEdit
          ? t("vendors:requirement.editTitle", { defaultValue: "Edit the request" })
          : t("vendors:requirement.addTitle", { defaultValue: "Ask {{name}} for a document", name: vendorName })
      }
      position="right"
      size="md"
      closable={!saving}
      loading={saving}
      errors={formatErrors(genericErrors)}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      isDirty={isDirty}
    >
      <Stack gap="md">
        <SelectField
          label={t("vendors:requirement.type", { defaultValue: "Document" })}
          data={VENDOR_DOCUMENT_TYPE_OPTIONS}
          value={form.documentType}
          onChange={(_n, v) => setForm((f) => ({ ...f, documentType: v ?? VENDOR_DOCUMENT_TYPE.INSURANCE }))}
          allowDeselect={false}
          error={fieldErrors.documentType}
          description={
            typeChangeDropsDocument
              ? t("vendors:requirement.typeChangeHint", { defaultValue: "The document they sent is another type — it will no longer answer this request." })
              : undefined
          }
          data-autofocus
          size="md"
        />

        <Stack gap={6}>
          <TextInput
            label={t("vendors:requirement.kind", { defaultValue: "What kind" })}
            placeholder={t("vendors:requirement.kindPlaceholder", { defaultValue: "e.g. General liability" })}
            value={form.documentKind}
            onChange={(e) => {
              const value = e.currentTarget.value;
              setForm((f) => ({ ...f, documentKind: value }));
            }}
            maxLength={VENDOR_LIMITS.KIND_MAX}
            error={fieldErrors.documentKind}
            size="md"
          />
          {suggestions.length > 0 ? (
            <Group gap="xs">
              {suggestions.map((s) => (
                <Chip key={s} size="md" checked={form.documentKind === s} onChange={() => setForm((f) => ({ ...f, documentKind: s }))}>
                  {s}
                </Chip>
              ))}
            </Group>
          ) : null}
        </Stack>

        <DatePickerInput
          label={t("vendors:requirement.due", { defaultValue: "Needed by" })}
          placeholder={t("vendors:requirement.duePlaceholder", { defaultValue: "No date" })}
          value={form.dueDate}
          onChange={(v) => setForm((f) => ({ ...f, dueDate: pickerDay(v) }))}
          valueFormat="MMM DD, YYYY"
          error={fieldErrors.dueDate}
          clearable
          size="md"
          {...responsiveDatePicker}
        />

        <Textarea
          label={t("vendors:requirement.instructions", { defaultValue: "Instructions for the vendor" })}
          description={t("vendors:requirement.instructionsHint", { defaultValue: "Shown to them on their upload page." })}
          placeholder={t("vendors:requirement.instructionsPlaceholder", {
            defaultValue: "e.g. At least $2,000,000 per occurrence; name the venue and the City as additional insured.",
          })}
          value={form.instructions}
          onChange={(e) => {
            const value = e.currentTarget.value;
            setForm((f) => ({ ...f, instructions: value }));
          }}
          autosize
          minRows={3}
          maxLength={VENDOR_LIMITS.INSTRUCTIONS_MAX}
          error={fieldErrors.instructions}
          size="md"
        />

        {!isEdit ? (
          <Text size="md" c="dimmed">
            {t("vendors:requirement.reminderNote", {
              defaultValue: "With a date, the vendor is reminded a week before it is due (unless you switch reminders off for them).",
            })}
          </Text>
        ) : null}
      </Stack>
    </AppDrawer>
  );
};
